import { Component, ElementRef, forwardRef, Input, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-image-upload',
  templateUrl: './image-upload.component.html',
  styleUrl: './image-upload.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ImageUploadComponent),
      multi: true,
    },
  ],
})
export class ImageUploadComponent implements ControlValueAccessor {
  @ViewChild('fileInputRef') fileInputRef!: ElementRef<HTMLInputElement>;

  @Input() fieldName = 'image';
  @Input() square = false;
  @Input() defaultImage = '';
  @Input() uploadLabel = '';
  @Input() uploadingLabel = '';

  value = '';
  isUploading = false;
  isDisabled = false;
  showUrlInput = false;
  urlInputValue = '';
  urlError = '';

  private readonly ACCEPTED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif', 'image/svg+xml'];
  private readonly MAX_IMAGE_SIZE = 2 * 1024 * 1024;
  private readonly URL_PATTERN = /^https?:\/\/.+\..+/i;

  private onChange: (val: string) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(
    private http: HttpClient,
    private translate: TranslateService,
  ) {}

  writeValue(val: string): void {
    this.value = val || '';
  }

  registerOnChange(fn: (val: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  get previewUrl(): string {
    return this.value || '';
  }

  triggerFileInput(): void {
    if (this.isDisabled || this.isUploading) return;
    this.fileInputRef?.nativeElement?.click();
    this.onTouched();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) return;

    if (!this.ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      alert(this.translate.instant('pages.chatStyling.form.logo.invalidType'));
      return;
    }
    if (file.size > this.MAX_IMAGE_SIZE) {
      alert(this.translate.instant('pages.chatStyling.form.logo.maxSize'));
      return;
    }

    this.isUploading = true;
    const formData = new FormData();
    formData.append('file', file);

    this.http
      .post<{ id: string; url: string }>(`${environment.filesBackend}/image/upload`, formData)
      .subscribe({
        next: (res) => {
          const imageUrl = `${environment.filesBackend}/image/${res.id}`;
          this.value = imageUrl;
          this.onChange(imageUrl);
          this.isUploading = false;
        },
        error: (err) => {
          console.error(`Image upload failed for ${this.fieldName}:`, err);
          alert(this.translate.instant('pages.chatStyling.form.logo.uploadError'));
          this.isUploading = false;
        },
      });

    input.value = '';
  }

  clear(): void {
    this.value = '';
    this.onChange('');
  }

  toggleUrlInput(): void {
    this.showUrlInput = !this.showUrlInput;
    this.urlInputValue = '';
    this.urlError = '';
  }

  saveUrl(): void {
    const url = this.urlInputValue.trim();
    if (!url) return;

    if (!this.URL_PATTERN.test(url)) {
      this.urlError = this.translate.instant('pages.chatStyling.form.logo.invalidUrl');
      return;
    }

    this.value = url;
    this.onChange(url);
    this.showUrlInput = false;
    this.urlInputValue = '';
    this.urlError = '';
  }
}
